import type { CalculatorState } from "./use-calculator-state";

const TRANSITION_TTL_MS = 60_000;
const TARGET_PATH = /^\/pt-br\/(?:eu|us)\/peptide-calculator(?:\/[a-z0-9]+(?:-[a-z0-9]+)*)?$/;

let pendingTransition: {
	pathname: string;
	search: string;
	hash: string;
	state: CalculatorState;
	expiresAt: number;
} | null = null;

function clearPendingTransition(): void {
	pendingTransition = null;
	if (typeof window === "undefined") return;
	window.removeEventListener("popstate", clearPendingTransition);
	window.removeEventListener("pagehide", clearPendingTransition);
}

/**
 * Carries live inputs across one explicitly requested pt -> pt-br navigation.
 * This stays in memory: ordinary loads must still reset the syringe barrel.
 */
export function prepareCalculatorLocaleTransition(targetPathname: string, state: CalculatorState): void {
	if (typeof window === "undefined") return;
	clearPendingTransition();
	if (!TARGET_PATH.test(targetPathname)) return;
	const sourcePathname = targetPathname.replace(/^\/pt-br\//, "/pt/");
	if (window.location.pathname !== sourcePathname) return;

	pendingTransition = {
		pathname: targetPathname,
		search: window.location.search,
		hash: window.location.hash,
		state: { ...state },
		expiresAt: Date.now() + TRANSITION_TTL_MS,
	};
	window.addEventListener("popstate", clearPendingTransition);
	window.addEventListener("pagehide", clearPendingTransition);
}

/** Only a calculator mount or Activity reveal at the exact destination can use this state. */
export function consumeCalculatorLocaleTransition(pathname: string): CalculatorState | null {
	if (typeof window === "undefined") return null;
	const transition = pendingTransition;
	if (!transition) return null;
	if (Date.now() >= transition.expiresAt) {
		clearPendingTransition();
		return null;
	}
	// Effects from another retained route must not consume the destination's state.
	if (transition.pathname !== pathname) return null;
	clearPendingTransition();
	if (transition.search !== window.location.search || transition.hash !== window.location.hash) return null;
	return transition.state;
}
